/**
 * Ülke ve saat dilimi listeleri.
 *
 * İsimleri sözlüğe yazmıyoruz: 249 ülkeyi üç dilde elle çevirmek hem
 * devasa hem de hataya açık. Tarayıcının Intl API'si bunu zaten biliyor
 * ve kullanıcının diline göre doğru ismi veriyor. Desteklemeyen eski
 * tarayıcılarda kodun kendisi gösteriliyor — bilgi kaybı yok.
 */

// ISO 3166-1 alpha-2
const COUNTRY_CODES = (
    'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ ' +
    'BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR ' +
    'CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
    'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ' +
    'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ ' +
    'LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
    'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF ' +
    'PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI ' +
    'SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR ' +
    'TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

// Intl.supportedValuesOf desteklenmeyen tarayıcılar için yedek liste.
// Tam IANA listesi değil; ürünün gerçekten kullanıldığı bölgeler.
const FALLBACK_ZONES = [
    'Europe/Istanbul', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Madrid', 'Europe/Rome',
    'Europe/Zurich', 'Europe/Vienna', 'Europe/Stockholm', 'Europe/Oslo',
    'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Warsaw', 'Europe/Prague',
    'Europe/Athens', 'Europe/Bucharest', 'Europe/Kyiv', 'Europe/Moscow',
    'Europe/Lisbon', 'Europe/Dublin',
    'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
    'America/Halifax', 'America/St_Johns',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'America/Mexico_City',
    'America/Bogota', 'America/Lima', 'America/Sao_Paulo', 'America/Buenos_Aires',
    'Africa/Cairo', 'Africa/Casablanca', 'Africa/Lagos', 'Africa/Nairobi',
    'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Baghdad', 'Asia/Tehran',
    'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta',
    'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Tokyo',
    'Asia/Baku', 'Asia/Tbilisi', 'Asia/Almaty', 'Asia/Tashkent', 'Asia/Jerusalem',
    'Australia/Perth', 'Australia/Adelaide', 'Australia/Brisbane',
    'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
    'UTC'
];

const BCP47 = { tr: 'tr-TR', en: 'en-GB', fr: 'fr-FR' };

// Kullanıcının kayıtlı değeri listede yoksa yine de görünmeli;
// aksi halde kaydet dediğinde farkında olmadan değiştirmiş oluyor.
function withCurrent(list, current) {
    if (current && list.indexOf(current) === -1) return [current].concat(list);
    return list;
}

// [{ code, label }] — kullanıcının dilinde, isme göre sıralı
export function countries(locale, current) {
    const tag = BCP47[locale] || 'en-GB';
    let display = null;
    try {
        display = new Intl.DisplayNames([tag], { type: 'region' });
    } catch (e) {
        // Eski tarayıcı: kodla devam
    }

    const list = withCurrent(COUNTRY_CODES, current).map(function (code) {
        let label = code;
        if (display) {
            try {
                label = display.of(code) || code;
            } catch (e) { /* tanınmayan kod */ }
        }
        return { code: code, label: label };
    });

    list.sort(function (a, b) { return a.label.localeCompare(b.label, tag); });
    return list;
}

// [{ code, label }] — "Europe/Istanbul (UTC+03:00)" biçiminde
export function timezones(locale, current) {
    const tag = BCP47[locale] || 'en-GB';

    let zones;
    try {
        zones = Intl.supportedValuesOf('timeZone');
    } catch (e) {
        zones = FALLBACK_ZONES;
    }
    if (!zones || !zones.length) zones = FALLBACK_ZONES;

    return withCurrent(zones, current).map(function (zone) {
        return { code: zone, label: zone.replace(/_/g, ' ') + offsetLabel(zone, tag) };
    });
}

// Saat farkını hesaplar: "Europe/Istanbul" yalnız başına bir şey
// söylemiyor, yanındaki UTC farkı seçimi kolaylaştırıyor.
function offsetLabel(zone, tag) {
    try {
        const parts = new Intl.DateTimeFormat(tag, {
            timeZone: zone,
            timeZoneName: 'shortOffset'
        }).formatToParts(new Date());
        const found = parts.find(function (p) { return p.type === 'timeZoneName'; });
        return found ? '  ' + found.value : '';
    } catch (e) {
        return '';
    }
}

// Tarayıcının kendi saat dilimi — yeni kullanıcıya varsayılan olarak
export function guessTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul';
    } catch (e) {
        return 'Europe/Istanbul';
    }
}

export default { countries, timezones, guessTimezone };



// WEBPACK FOOTER //
// ./src/config/geo.js